/**
 * useLiveSpeech - Real-time Speech-to-Text Hook
 * 
 * Streams audio via WebSocket and provides progressive transcript updates.
 * Provider-agnostic: works with Abair now, can support OpenAI RT later.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { STT_RECORDING_STARTED_EVENT, stopAllAudioPlayback } from '../services/audioService';
import type { VoiceInputMessages } from './voiceInputMessages';

export type LiveSpeechProvider = 'abair' | 'openai';
export type LiveSpeechState = 'idle' | 'connecting' | 'recording' | 'processing';

interface UseLiveSpeechOptions {
  provider: LiveSpeechProvider;
  websocketUrl: string;
  onFinalTranscript?: (text: string) => void;
  onError?: (error: string) => void;
  onAnnounce?: (message: string) => void;
  messages: VoiceInputMessages;
}

interface UseLiveSpeechReturn {
  state: LiveSpeechState;
  partialText: string;
  finalText: string;
  elapsedSeconds: number;
  startRecording: () => void;
  stopRecording: () => void;
  cancelRecording: () => void;
  isRecording: boolean;
  isConnecting: boolean;
  timerDisplay: string;
}

export function useLiveSpeech({
  provider,
  websocketUrl,
  onFinalTranscript,
  onError,
  onAnnounce,
  messages,
}: UseLiveSpeechOptions): UseLiveSpeechReturn {
  const [state, setState] = useState<LiveSpeechState>('idle');
  const [partialText, setPartialText] = useState('');
  const [finalText, setFinalText] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stateRef = useRef<LiveSpeechState>('idle');
  const partialTextRef = useRef<string>('');
  const finalTextRef = useRef<string>('');

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startRecording = useCallback(async () => {
    if (state !== 'idle') return;

    try {
      // Guardrail: always stop active TTS/audio at the point microphone capture starts.
      stopAllAudioPlayback();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(STT_RECORDING_STARTED_EVENT));
      }
      setState('connecting');
      stateRef.current = 'connecting';
      setPartialText('');
      partialTextRef.current = '';
      setFinalText('');
      finalTextRef.current = '';
      setElapsedSeconds(0);

      // Request microphone with audio quality enhancements for better recognition
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,      // Reduces echo from speakers
          noiseSuppression: true,      // Filters background noise
          autoGainControl: true,       // Normalizes volume levels
        }
      });
      mediaStreamRef.current = stream;

      // Create AudioContext at 16kHz for Abair
      audioContextRef.current = new AudioContext({ sampleRate: 16000 });
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Load AudioWorklet processor
      await audioContextRef.current.audioWorklet.addModule('/abair-audio-processor.js');
      
      workletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current,
        'abair-audio-processor'
      );
      source.connect(workletNodeRef.current);

      // Open WebSocket
      websocketRef.current = new WebSocket(websocketUrl);

      websocketRef.current.onopen = () => {
        console.log('[Abair] WebSocket connected');
        // Send config message
        if (provider === 'abair') {
          console.log('[Abair] Sending config:', { sample_rate: 16000 });
          websocketRef.current?.send(JSON.stringify({ config: { sample_rate: 16000 } }));
        }
        
        setState('recording');
        stateRef.current = 'recording';
        onAnnounce?.(messages.recordingAnnouncement);

        // Start timer
        timerRef.current = setInterval(() => {
          setElapsedSeconds(prev => prev + 1);
        }, 1000);

        if (workletNodeRef.current) {
          workletNodeRef.current.port.onmessage = (event) => {
            const pcmData = event.data as Int16Array;
            if (websocketRef.current?.readyState === WebSocket.OPEN) {
              console.log('[Abair] Sending audio chunk:', pcmData.length, 'samples');
              websocketRef.current.send(pcmData.buffer);
            }
          };
        }
      };

      websocketRef.current.onmessage = (event) => {
        try {
          // Handle response - Abair sends JSON with partial/final transcripts
          if (typeof event.data === 'string') {
            const data = JSON.parse(event.data);
            
            // Update partial or final based on response
            if (data.text || data.partial) {
              const text = data.text || data.partial;
              setPartialText(text);
              partialTextRef.current = text;
              
              // If marked as final, also update finalText
              if (data.isFinal || data.final) {
                setFinalText(text);
                finalTextRef.current = text;
              }
            }
          }
        } catch {
          // Non-JSON response - treat as partial text
          if (typeof event.data === 'string' && event.data.trim()) {
            setPartialText(event.data.trim());
            partialTextRef.current = event.data.trim();
          }
        }
      };

      websocketRef.current.onerror = (err) => {
        console.error('[Abair] WebSocket error:', err);
        onError?.(messages.transcriptionFailedMessage);
        cleanup();
        setState('idle');
        stateRef.current = 'idle';
      };

      websocketRef.current.onclose = () => {
        // Get final text when connection closes - use refs to avoid stale closure
        if (stateRef.current === 'recording' || stateRef.current === 'processing') {
          // Use partial text as final if no explicit final received
          const text = finalTextRef.current || partialTextRef.current;
          if (text && onFinalTranscript) {
            onFinalTranscript(text);
          }
          if (text) {
            onAnnounce?.(`${messages.transcriptionResultPrefix} ${text.trim()}`);
          }
        }
        cleanup();
        setState('idle');
        stateRef.current = 'idle';
      };

    } catch (err) {
      let errorMsg = messages.microphoneAccessFailedMessage;
      if (err instanceof Error && err.name === 'NotAllowedError') {
        errorMsg = messages.microphoneAccessDeniedMessage;
      } else if (err instanceof Error && err.name === 'NotFoundError') {
        errorMsg = messages.noMicrophoneDetectedMessage;
      }
      onError?.(errorMsg);
      cleanup();
      setState('idle');
    }
  }, [state, provider, websocketUrl, onFinalTranscript, onError, onAnnounce, messages, cleanup]);

  const stopRecording = useCallback(() => {
    if (state !== 'recording') return;
    
    setState('processing');
    stateRef.current = 'processing';

    // Close WebSocket to trigger final response
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.close();
    } else {
      cleanup();
      setState('idle');
      stateRef.current = 'idle';
    }
  }, [state, cleanup]);

  const cancelRecording = useCallback(() => {
    cleanup();
    setPartialText('');
    partialTextRef.current = '';
    setFinalText('');
    finalTextRef.current = '';
    setElapsedSeconds(0);
    setState('idle');
    stateRef.current = 'idle';
    onAnnounce?.(messages.recordingCancelledAnnouncement);
  }, [cleanup, messages.recordingCancelledAnnouncement, onAnnounce]);

  return {
    state,
    partialText,
    finalText,
    elapsedSeconds,
    startRecording,
    stopRecording,
    cancelRecording,
    isRecording: state === 'recording',
    isConnecting: state === 'connecting',
    timerDisplay: `${elapsedSeconds}s`,
  };
}
